import { Button, Checkbox, Input, List, Modal, Select, Table, TableColumnsType, Tag, Typography } from "antd";
import { ArrowDownAZ, ArrowUpAZ, Box, Package, Plus, Search, X } from "lucide-react";
import { FC, useCallback, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useGetInventoriesInfinite } from "~/services/inventory";
import { SortByField } from "~/services/inventory/inventory.types";
import { useGetMyServiceTicketInventories } from "~/services/service-ticket";
import { useBooking } from "~/store";
import { TInventory } from "~/types";
import { calculateCubicVolume, capitalize, cn } from "~/utils";
import {
  CheckType,
  InventoryStatusCode,
  InventoryType,
  PackingTypes,
  SortOrder,
} from "~/utils/constants";

const { Text } = Typography;

const getStatusColor = (status: InventoryStatusCode) => {
  switch (status) {
    case InventoryStatusCode.AVAILABLE_LIVE:
      return "green";
    case InventoryStatusCode.AVAILABLE_ON_HAND:
      return "blue";
    case InventoryStatusCode.NO_POSSESSION:
      return "orange";
    case InventoryStatusCode.RELEASED:
      return "purple";
    default:
      return "default";
  }
};

const formatPackingType = (packingType?: PackingTypes) => {
  if (!packingType) return "N/A";
  const packingMap: Record<PackingTypes, string> = {
    [PackingTypes.LOOSE]: "Loose",
    [PackingTypes.SLIPCASE]: "Slipcase",
    [PackingTypes.CB_FOLD]: "Cardboard FOLD",
    [PackingTypes.SHADOW_BOX]: "Shadow Box",
    [PackingTypes.FLOATING_SHADOW_BOX]: "Floating Shadow Box",
    [PackingTypes.POLY_ONLY]: "Poly Only",
    [PackingTypes.TRAVEL_FRAME]: "Travel Frame",
    [PackingTypes.CRATE]: "Crate",
    [PackingTypes.SLATCRATE]: "Slat Crate",
    [PackingTypes.PALLET]: "Pallet",
    [PackingTypes.COMMERCIAL_BIN]: "Commercial Bin",
    [PackingTypes.CBIN]: "C-Bin",
    [PackingTypes.UNKNOWN_PACKAGING]: "Unknown Packaging",
    [PackingTypes.BOX]: "Box",
    [PackingTypes.CUSTOM]: "Custom",
    [PackingTypes.T_Frame]: "T-Frame",
    [PackingTypes.BLANKET_WRAPPED]: "Blanket Wrapped",
    [PackingTypes.POLY_FOAM]: "Poly+Foam",
    [PackingTypes.POLY_FOAM_CB]: "Poly+Foam+CB",
  };
  return packingMap[packingType] || packingType;
};

const calculateCubicFeet = (inventory: TInventory) => {
  if (
    inventory.length != null &&
    inventory.width != null &&
    inventory.height != null &&
    inventory.length > 0 &&
    inventory.width > 0 &&
    inventory.height > 0
  ) {
    const cubicFeet = calculateCubicVolume(inventory) / 1728;
    return cubicFeet.toFixed(2);
  }
  return "N/A";
};

const STATUS_OPTIONS = [
  { label: "All Statuses", value: "" },
  { label: "Available (Live)", value: InventoryStatusCode.AVAILABLE_LIVE },
  { label: "Available (On Hand)", value: InventoryStatusCode.AVAILABLE_ON_HAND },
  { label: "No Possession", value: InventoryStatusCode.NO_POSSESSION },
  { label: "Released", value: InventoryStatusCode.RELEASED },
];

const SORT_OPTIONS = [
  { label: "Reference #", value: SortByField.INVENTORY_REF },
  { label: "Title", value: SortByField.TITLE },
  { label: "Artist", value: SortByField.ARTIST_NAME },
  { label: "Client", value: SortByField.CLIENT_NAME },
  { label: "Date Added", value: SortByField.CREATED_AT },
  { label: "Packing Type", value: SortByField.PACKING_TYPE },
  { label: "Status", value: SortByField.STATUS },
];

type Props = {
  ticketId: string;
  value?: string[];
  onChange?: (value: string[]) => void;
  showStats?: boolean;
};

export const AssociatedInventory: FC<Props> = ({
  value,
  onChange,
  ticketId,
  showStats = false,
}) => {
  const { clientId, booking, viewMode } = useBooking();
  const viewOnly = useMemo(() => viewMode === "view", [viewMode]);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<InventoryStatusCode | undefined>(undefined);
  const [sortBy, setSortBy] = useState<SortByField | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);

  const { data: ticketData } = useGetMyServiceTicketInventories(ticketId);

  const {
    data: inventoriesData,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useGetInventoriesInfinite({
    checkParentId: CheckType.CHECK,
    pageSize: 50,
    clientId: clientId ?? booking?.clientId ?? "",
    search: searchTerm || undefined,
    status: statusFilter,
    sortBy,
    sortOrder: sortBy ? sortOrder : undefined,
  });

  const inventories = useMemo(() => {
    return inventoriesData?.pages.flatMap((page) => page.items) ?? [];
  }, [inventoriesData]);

  const totalInventoryCount = useMemo(() => {
    return inventoriesData?.pages[0]?.meta?.totalItems ?? 0;
  }, [inventoriesData]);

  // Calculate stats for selected inventory
  const stats = useMemo(() => {
    if (!value || value.length === 0) {
      return { count: 0, volume: 0, weight: 0 };
    }

    let totalVolume = 0;
    let totalWeight = 0;

    value.forEach((id) => {
      const inventory = ticketData?.inventories?.find((inv) => inv.id === id)
        ?? inventories?.find((inv) => inv.id === id);

      if (inventory) {
        // Calculate volume in cubic feet
        if (inventory.length && inventory.width && inventory.height) {
          const cubicInches = inventory.length * inventory.width * inventory.height;
          totalVolume += cubicInches / 1728; // Convert to cubic feet
        }
        // Add weight
        if (inventory.weight) {
          totalWeight += Number(inventory.weight);
        }
      }
    });

    return {
      count: value.length,
      volume: totalVolume,
      weight: totalWeight,
    };
  }, [value, ticketData?.inventories, inventories]);

  return (
    <div className="border border-gray-300 rounded-lg p-3 bg-white shadow-sm">
      {/* Stats Header */}
      {showStats && stats.count > 0 && (
        <div className="flex items-center gap-4 mb-3 pb-3 border-b border-gray-200 text-xs text-gray-600">
          <span><strong className="text-gray-900">{stats.count}</strong> item{stats.count !== 1 ? 's' : ''}</span>
          <span><strong className="text-gray-900">{stats.volume.toFixed(1)}</strong> ft³</span>
          <span><strong className="text-gray-900">{stats.weight.toFixed(0)}</strong> lbs</span>
        </div>
      )}
      <div className={value && value.length > 10 ? "max-h-80 overflow-y-auto" : ""}>
        <List
          size="small"
          dataSource={value}
        renderItem={(item) => {
          const addedInventory = ticketData?.inventories?.find(
            (inventory) => inventory.id === item
          );

          const newAddedInventory = inventories?.find(
            (inventory) => inventory.id === item
          );

          const inventory = addedInventory ?? newAddedInventory;

          const Icon =
            inventory?.itemType === InventoryType.PACKAGE ? Box : Package;

          if (!inventory) return null;

          return (
            <List.Item className="flex justify-between p-0 not-first:pt-2 pb-2">
              <List.Item.Meta
                title={
                  <div className="flex items-center">
                    <Icon
                      size={16}
                      className={cn(
                        inventory.itemType === InventoryType.PACKAGE
                          ? "text-amber-500"
                          : "text-green-500",
                        "mr-1"
                      )}
                    />
                    <span className="font-semibold text-sm">
                      {capitalize(inventory.itemType)}
                    </span>
                    <span className="mx-1">-</span>
                    {inventory.title}
                    <span className="mx-1">-</span>
                    <span className="text-xs text-gray-500 font-normal">
                      ID: {inventory.inventoryRef}
                    </span>
                  </div>
                }


description={
  <div className="flex flex-wrap items-center text-xs gap-x-2">
    <div className="flex items-center">
      <span className="text-gray-500 mr-1">Client Ref:</span>
      <span>{inventory?.clientInternalRef || "N/A"}</span>
    </div>
    <div className="mx-1">|</div>
    <div className="flex items-center">
      <span className="text-gray-500 mr-1">Artist:</span>
      <span>{inventory?.artistName || "N/A"}</span>
    </div>
    <div className="mx-1">|</div>
    <div className="flex items-center">
      <span className="text-gray-500 mr-1">Packing:</span>
      <span>{formatPackingType(inventory.packingType)}</span>
    </div>
    <div className="mx-1">|</div>
    <div className="flex items-center">
      <span className="text-gray-500 mr-1">Dimensions:</span>
      <span>{inventory?.length || "N/A"} x {inventory?.width || "N/A"} x {inventory?.height || "N/A"} H</span>
    </div>
    <div className="mx-1">|</div>
    <div className="flex items-center">
      <span className="text-gray-500 mr-1">Size:</span>
      <span>{calculateCubicFeet(inventory)} ft³</span>
    </div>
    <div className="mx-1">|</div>
    <Tag color={getStatusColor(inventory.status)}>
      {capitalize(inventory.status)}
    </Tag>
  </div>
                }
              />

              {!viewOnly && (
                <X
                  size={14}
                  className="ml-1 cursor-pointer text-red-500"
                  onClick={() => {
                    if (value) {
                      const updatedInventories = value.filter(
                        (inv) => inv !== inventory.id
                      );
                      onChange?.(updatedInventories);
                    }
                  }}
                />
              )}
            </List.Item>
          );
        }}
      />
      </div>
      <Button
        onClick={() => {
          setOpen(true);
          setSearchTerm("");
        }}
        className="w-full px-3 py-2 border rounded hover:bg-gray-50 text-sm flex items-center justify-center"
      >
        <Plus size={14} className="mr-1" /> Add Inventory
      </Button>
      <InventoryModal
        open={open}
        setOpen={setOpen}
        value={value}
        onChange={onChange}
        inventories={inventories}
        totalInventoryCount={totalInventoryCount}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isLoading={isLoading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
      />
    </div>
  );
};

const InventoryModal = ({
  open,
  setOpen,
  value,
  onChange,
  inventories,
  totalInventoryCount,
  fetchNextPage,
  hasNextPage,
  isLoading,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  value?: string[];
  onChange?: (value: string[]) => void;
  inventories: TInventory[];
  totalInventoryCount: number;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isLoading: boolean;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  statusFilter: InventoryStatusCode | undefined;
  setStatusFilter: (value: InventoryStatusCode | undefined) => void;
  sortBy: SortByField | undefined;
  setSortBy: (value: SortByField | undefined) => void;
  sortOrder: SortOrder;
  setSortOrder: (value: SortOrder) => void;
}) => {
  // Checked IDs persist across search/filter changes
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  // Separate selected and unselected inventories, with selected on top
  const sortedInventories = useMemo(() => {
    const selected = inventories.filter((inv) => value?.includes(inv.id));
    const unselected = inventories.filter((inv) => !value?.includes(inv.id));
    return [...selected, ...unselected];
  }, [inventories, value]);

  const hasActiveFilters = !!statusFilter || !!sortBy;

  // Check state for visible items
  const visibleIds = useMemo(() => sortedInventories.map((inv) => inv.id), [sortedInventories]);
  const visibleCheckedCount = useMemo(() => visibleIds.filter((id) => checkedIds.has(id)).length, [visibleIds, checkedIds]);
  const allVisibleChecked = visibleIds.length > 0 && visibleCheckedCount === visibleIds.length;
  const someVisibleChecked = visibleCheckedCount > 0 && !allVisibleChecked;

  // Bulk action counts
  const checkedToAdd = useMemo(() => {
    return [...checkedIds].filter((id) => !value?.includes(id));
  }, [checkedIds, value]);
  const checkedToRemove = useMemo(() => {
    return [...checkedIds].filter((id) => value?.includes(id));
  }, [checkedIds, value]);

  const toggleCheck = useCallback((id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleChecked) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allVisibleChecked, visibleIds]);

  const handleBulkAdd = useCallback(() => {
    if (checkedToAdd.length === 0) return;
    const updated = [...(value ?? []), ...checkedToAdd];
    onChange?.(updated);
    setCheckedIds((prev) => {
      const next = new Set(prev);
      checkedToAdd.forEach((id) => next.delete(id));
      return next;
    });
  }, [checkedToAdd, value, onChange]);

  const handleBulkRemove = useCallback(() => {
    if (checkedToRemove.length === 0) return;
    const removeSet = new Set(checkedToRemove);
    const updated = (value ?? []).filter((id) => !removeSet.has(id));
    onChange?.(updated);
    setCheckedIds((prev) => {
      const next = new Set(prev);
      checkedToRemove.forEach((id) => next.delete(id));
      return next;
    });
  }, [checkedToRemove, value, onChange]);

  const columns: TableColumnsType<TInventory> = [
    {
      title: (
        <Checkbox
          checked={allVisibleChecked}
          indeterminate={someVisibleChecked}
          onChange={toggleAllVisible}
        />
      ),
      key: "checkbox",
      width: 48,
      render: (_, record) => (
        <Checkbox
          checked={checkedIds.has(record.id)}
          onChange={() => toggleCheck(record.id)}
        />
      ),
    },
    {
      title: "Art Handler ID",
      dataIndex: "inventoryRef",
      key: "id",
      width: "9%",
    },
    {
      title: "Client Ref",
      dataIndex: "clientInternalRef",
      key: "clientInternalRef",
      width: "9%",
      render: (value) => value || "N/A",
    },
    {
      title: "Artist",
      dataIndex: "artistName",
      key: "artistName",
      width: "13%",
      render: (value) => value || "N/A",
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: "18%",
    },
    {
      title: "Type",
      dataIndex: "itemType",
      key: "type",
      width: "8%",
      render: (type: InventoryType) => {
        const Icon = type === InventoryType.PACKAGE ? Box : Package;
        return (
          <div className="flex items-center">
            <Icon
              size={16}
              className={cn(
                type === InventoryType.PACKAGE
                  ? "text-amber-500"
                  : "text-green-500",
                "mr-1"
              )}
            />
            <span>{capitalize(type)}</span>
          </div>
        );
      },
    },
    {
      title: "Packing",
      dataIndex: "packingType",
      key: "packing",
      width: "9%",
      render: (packingType) => formatPackingType(packingType),
    },
    {
      title: "Dimensions",
      key: "dimensions",
      width: "11%",
      render: (_, record) => {
        const length = record.length || "N/A";
        const width = record.width || "N/A";
        const height = record.height || "N/A";
        return `${length} x ${width} x ${height} H`;
      },
    },
    {
      title: "Size (ft³)",
      key: "size",
      width: "8%",
      render: (_, record) => calculateCubicFeet(record),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "9%",
      render: (status) => (
        <Tag color={getStatusColor(status)}>{capitalize(status)}</Tag>
      ),
    },
    {
      title: "Action",
      dataIndex: "",
      key: "x",
      align: "center",
      width: "8%",
      render: (_, inventory) => {
        const isSelected = value?.includes(inventory.id);
        return (
          <Button
            size="small"
            htmlType="button"
            onClick={() => {
              if (isSelected) {
                const updatedInventories = value?.filter(
                  (inv) => inv !== inventory.id
                );
                onChange?.(updatedInventories ?? []);
              } else {
                const updatedInventories = [...(value ?? []), inventory.id];
                onChange?.(updatedInventories);
              }
            }}
            type={isSelected ? "default" : "primary"}
            className={`px-3 py-1 border rounded-2xl`}
          >
            {isSelected ? "Remove" : "Add"}
          </Button>
        );
      },
    },
  ];

  return (
    <Modal
      title={
        <div className="flex items-center justify-between border-b border-gray-200 pb-3 -mx-6 px-6">
          <span className="text-lg font-semibold text-gray-900">Select Inventory</span>
          <div className="text-sm font-normal text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
            Selected: <span className="font-semibold text-blue-600">{value?.length || 0}</span>
            <span className="mx-2">&bull;</span>
            Total: <span className="font-semibold text-gray-700">{totalInventoryCount}</span>
            {searchTerm && (
              <span className="ml-2">
                &bull; Search results: <span className="font-semibold text-green-600">{inventories.length}</span>
              </span>
            )}
          </div>
        </div>
      }
      open={open}
      onCancel={() => {
        setOpen(false);
        setSearchTerm("");
        setCheckedIds(new Set());
      }}
      footer={null}
      width={"90%"}
      className="inventory-selection-modal"
    >
      {/* Search & Filters */}
      <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
        <Input
          placeholder="Search by client ref, inventory number, artist, status, dimensions, packing type, or title..."
          prefix={<Search size={14} className="text-gray-400" />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          className="w-full shadow-sm"
          size="large"
        />

        {/* Filter & Sort Row */}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Select
            placeholder="Filter by status"
            value={statusFilter ?? ""}
            onChange={(val) => setStatusFilter(val || undefined)}
            options={STATUS_OPTIONS}
            className="w-48"
            size="middle"
          />

          <Select
            placeholder="Sort by..."
            value={sortBy}
            onChange={(val) => setSortBy(val || undefined)}
            options={SORT_OPTIONS}
            className="w-40"
            size="middle"
            allowClear
          />

          {sortBy && (
            <Button
              size="middle"
              icon={sortOrder === SortOrder.ASC ? <ArrowDownAZ size={14} /> : <ArrowUpAZ size={14} />}
              onClick={() => setSortOrder(sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC)}
              className="flex items-center gap-1"
            >
              {sortOrder === SortOrder.ASC ? "Ascending" : "Descending"}
            </Button>
          )}

          {hasActiveFilters && (
            <Button
              size="middle"
              type="link"
              onClick={() => {
                setStatusFilter(undefined);
                setSortBy(undefined);
                setSortOrder(SortOrder.ASC);
              }}
              className="text-gray-500 p-0 h-auto"
            >
              Clear filters
            </Button>
          )}
        </div>

        {value && value.length > 0 && (
          <div className="mt-2 flex items-center justify-between">
            <Text type="secondary" className="text-sm">
              {value.length} item{value.length !== 1 ? 's' : ''} selected
              {searchTerm && ` \u2022 ${sortedInventories.length} results`}
            </Text>
            {searchTerm && (
              <Button
                size="small"
                type="link"
                onClick={() => setSearchTerm("")}
                className="text-blue-600 p-0 h-auto"
              >
                Clear search
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {checkedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
          <Text className="text-sm font-medium text-blue-800">
            {checkedIds.size} checked
          </Text>
          {checkedToAdd.length > 0 && (
            <Button size="small" type="primary" onClick={handleBulkAdd}>
              Add {checkedToAdd.length} item{checkedToAdd.length !== 1 ? 's' : ''}
            </Button>
          )}
          {checkedToRemove.length > 0 && (
            <Button size="small" danger onClick={handleBulkRemove}>
              Remove {checkedToRemove.length} item{checkedToRemove.length !== 1 ? 's' : ''}
            </Button>
          )}
          <Button
            size="small"
            type="link"
            onClick={() => setCheckedIds(new Set())}
            className="text-gray-500 ml-auto"
          >
            Clear checks
          </Button>
        </div>
      )}

      <InfiniteScroll
        dataLength={sortedInventories.length}
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        loader={<div className="text-center py-4">Loading more items...</div>}
        height={500}
        endMessage={
          <div className="text-center py-4 text-gray-500">
            {sortedInventories.length === 0
              ? searchTerm
                ? "No inventory items found matching your search"
                : "No inventory items found"
              : "All items loaded"}
          </div>
        }
      >
        <Table
          className="rounded-lg"
          rowKey={(row) => row.id}
          columns={columns}
          dataSource={sortedInventories}
          pagination={false}
          loading={isLoading && inventories.length === 0}
          rowClassName={(record) =>
            cn(
              value?.includes(record.id) && "bg-blue-50 border-l-4 border-l-blue-500",
              checkedIds.has(record.id) && !value?.includes(record.id) && "bg-gray-50",
            )
          }
        />
      </InfiniteScroll>
    </Modal>
  );
};
